/**
 * Descriptor de Processo judicial. Todos os campos de `ProcessoOut` (nada oculto).
 * As partes e movimentações — que vêm do DataJud — aparecem num painel próprio
 * (leitura). Documentos anexos também.
 */
import { Gavel } from 'lucide-react';
import { PainelProcessoRelacoes } from '@/components/crud/paineis/PainelProcessoRelacoes';
import type { ProcessoCad } from '@/lib/api';
import { definirRecurso } from '@/lib/recursos/tipos';

export const processo = definirRecurso<ProcessoCad>({
  chave: 'processos',
  endpoint: '/cadastro/processos',
  ownerTipo: 'processo',
  titulo: 'Processos',
  tituloSingular: 'processo',
  descricao: 'Processos judiciais (DataJud) correlacionados com as partes do cadastro.',
  icone: Gavel,
  capacidades: { podeEditar: true, podeExcluir: true },
  buscaPlaceholder: 'Buscar por número CNJ ou classe',
  rotuloItem: (p) => p.numero_cnj || 'Processo',

  colunas: [
    { chave: 'numero_cnj', rotulo: 'Número CNJ', mono: true },
    { chave: 'classe_nome', rotulo: 'Classe', ocultarEm: 'sm' },
    { chave: 'tribunal', rotulo: 'Tribunal', ocultarEm: 'md' },
  ],

  camposCriar: [
    { chave: 'numero_cnj', rotulo: 'Número CNJ', tipo: 'texto' },
    { chave: 'classe_nome', rotulo: 'Classe', tipo: 'texto' },
  ],

  gruposEdicao: [
    {
      id: 'identificacao',
      titulo: 'Identificação',
      campos: [
        { chave: 'numero_cnj', rotulo: 'Número CNJ', tipo: 'texto', colSpan: 2 },
        { chave: 'numero_antigo', rotulo: 'Número antigo', tipo: 'texto' },
        { chave: 'classe_codigo', rotulo: 'Código da classe', tipo: 'texto' },
        { chave: 'classe_nome', rotulo: 'Classe', tipo: 'texto', colSpan: 2 },
        { chave: 'area', rotulo: 'Área', tipo: 'texto' },
        { chave: 'valor_causa', rotulo: 'Valor da causa (R$)', tipo: 'numero' },
      ],
    },
    {
      id: 'jurisdicao',
      titulo: 'Jurisdição',
      campos: [
        { chave: 'segmento', rotulo: 'Segmento', tipo: 'texto' },
        { chave: 'tribunal', rotulo: 'Tribunal', tipo: 'texto' },
        { chave: 'grau', rotulo: 'Grau', tipo: 'texto' },
        { chave: 'orgao_julgador', rotulo: 'Órgão julgador', tipo: 'texto' },
        { chave: 'comarca', rotulo: 'Comarca', tipo: 'texto' },
        { chave: 'uf', rotulo: 'UF', tipo: 'texto' },
        { chave: 'juiz_relator', rotulo: 'Juiz/Relator', tipo: 'texto' },
        { chave: 'sistema', rotulo: 'Sistema', tipo: 'texto' },
      ],
    },
    {
      id: 'andamento',
      titulo: 'Andamento',
      campos: [
        { chave: 'situacao', rotulo: 'Situação', tipo: 'texto' },
        { chave: 'fase', rotulo: 'Fase', tipo: 'texto' },
        { chave: 'prioridade', rotulo: 'Prioridade', tipo: 'texto' },
        { chave: 'data_distribuicao', rotulo: 'Distribuição', tipo: 'data' },
        { chave: 'data_autuacao', rotulo: 'Autuação', tipo: 'data' },
        { chave: 'data_arquivamento', rotulo: 'Arquivamento', tipo: 'data' },
        { chave: 'segredo_justica', rotulo: 'Segredo de justiça', tipo: 'booleano' },
        { chave: 'justica_gratuita', rotulo: 'Justiça gratuita', tipo: 'booleano' },
      ],
    },
    {
      id: 'sistema',
      titulo: 'Fonte e observações',
      campos: [
        { chave: 'fonte', rotulo: 'Fonte', tipo: 'texto' },
        { chave: 'url', rotulo: 'URL', tipo: 'texto', colSpan: 2 },
        { chave: 'ativo', rotulo: 'Ativo', tipo: 'booleano' },
        { chave: 'observacoes', rotulo: 'Observações', tipo: 'texto-longo', colSpan: 2 },
        { chave: 'created_at', rotulo: 'Criado em', tipo: 'leitura' },
        { chave: 'updated_at', rotulo: 'Atualizado em', tipo: 'leitura' },
      ],
    },
  ],

  paineis: [
    {
      tipo: 'custom',
      id: 'processo-relacoes',
      titulo: 'Partes e movimentações',
      render: ({ item }) => <PainelProcessoRelacoes processoId={item.id} />,
    },
    { tipo: 'documentos' },
    { tipo: 'dossie' },
  ],

  // Processo não tem contato próprio; as abas cobrem os dados, as partes/movimentações (DataJud),
  // documentos e a correlação (dossiê + grafo, ancorados nas partes do processo).
  abas: [
    {
      id: 'dados',
      rotulo: 'Dados do Processo',
      grupos: ['identificacao', 'jurisdicao', 'andamento', 'sistema'],
    },
    {
      id: 'partes',
      rotulo: 'Partes e Movimentações',
      paineis: [
        {
          tipo: 'custom',
          id: 'processo-relacoes',
          titulo: 'Partes e movimentações',
          render: ({ item }) => <PainelProcessoRelacoes processoId={item.id} />,
        },
      ],
    },
    { id: 'documentos', rotulo: 'Documentos', paineis: [{ tipo: 'documentos' }] },
    { id: 'dossie', rotulo: 'Dossiê', paineis: [{ tipo: 'dossie' }] },
    { id: 'grafo', rotulo: 'Grafo', paineis: [{ tipo: 'grafo' }] },
  ],
});
